export type Vec3 = [number, number, number];

export interface Pose {
  position: Vec3;
  target: Vec3;
  fov?: number;
}

export interface Spherical {
  azimuth: number;
  polar: number;
  radius: number;
}

export interface OrbitBounds {
  minPolar?: number;
  maxPolar?: number;
  minDistance?: number;
  maxDistance?: number;
  /** Horizontal bounds in radians; both undefined = unconstrained azimuth. */
  minAzimuth?: number;
  maxAzimuth?: number;
}

const TWO_PI = Math.PI * 2;

/** Wrap an angle into [-π, π]. */
export function wrapAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= TWO_PI;
  while (x < -Math.PI) x += TWO_PI;
  return x;
}

/**
 * Shortest signed delta from `from` to `to` on the circle (-π..π).
 * Used for azimuth interpolation so we always rotate the short way around.
 */
export function shortestAngleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

/**
 * Clamp azimuth to a contiguous allowed arc. If min and max are both set:
 *  - min < max  → allowed range is [min, max] (e.g., -1 to 1 = a 2-rad fan).
 *  - min > max  → allowed range wraps the seam (e.g., 2.0 to -2.0 = the back).
 * If only one is set, clamp on that side; if neither, return unchanged.
 */
function clampAzimuth(az: number, min: number | undefined, max: number | undefined): number {
  if (min === undefined && max === undefined) return az;
  const a = wrapAngle(az);
  if (min !== undefined && max !== undefined) {
    if (min <= max) {
      // Simple range, no seam wrap
      if (a >= min && a <= max) return a;
      // Snap to nearer endpoint by signed-shortest distance
      const dMin = Math.abs(shortestAngleDelta(a, min));
      const dMax = Math.abs(shortestAngleDelta(a, max));
      return dMin < dMax ? min : max;
    }
    // Wrapped range: allowed is [min, π] ∪ [-π, max]
    if (a >= min || a <= max) return a;
    const dMin = Math.abs(shortestAngleDelta(a, min));
    const dMax = Math.abs(shortestAngleDelta(a, max));
    return dMin < dMax ? min : max;
  }
  if (min !== undefined) {
    // One-sided: must be ≥ min on the short-arc side
    return shortestAngleDelta(min, a) >= 0 ? a : min;
  }
  // max !== undefined
  return shortestAngleDelta(a, max!) >= 0 ? a : max!;
}

export interface InertiaState extends Spherical {
  vAz: number;
  vPo: number;
  vR: number;
}

const VEL_EPS = 1e-6;

export function sphericalToPose(target: Vec3, s: Spherical): Pose {
  const { azimuth, polar, radius } = s;
  const sinPolar = Math.sin(polar);
  const x = radius * sinPolar * Math.cos(azimuth);
  const y = radius * Math.cos(polar);
  const z = radius * sinPolar * Math.sin(azimuth);
  return {
    position: [target[0] + x, target[1] + y, target[2] + z],
    target: [...target] as Vec3
  };
}

export function poseToSpherical(pose: Pose, target: Vec3): Spherical {
  const dx = pose.position[0] - target[0];
  const dy = pose.position[1] - target[1];
  const dz = pose.position[2] - target[2];
  const radius = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const polar = Math.acos(Math.max(-1, Math.min(1, dy / Math.max(radius, 1e-9))));
  const azimuth = Math.atan2(dz, dx);
  return { azimuth, polar, radius };
}

export function lerpPose(a: Pose, b: Pose, t: number): Pose {
  const u = Math.max(0, Math.min(1, t));
  const lerp = (x: number, y: number) => x + (y - x) * u;
  const result: Pose = {
    position: [
      lerp(a.position[0], b.position[0]),
      lerp(a.position[1], b.position[1]),
      lerp(a.position[2], b.position[2])
    ],
    target: [
      lerp(a.target[0], b.target[0]),
      lerp(a.target[1], b.target[1]),
      lerp(a.target[2], b.target[2])
    ]
  };
  if (a.fov !== undefined && b.fov !== undefined) {
    result.fov = lerp(a.fov, b.fov);
  } else if (a.fov !== undefined) {
    result.fov = a.fov;
  } else if (b.fov !== undefined) {
    result.fov = b.fov;
  }
  return result;
}

export function clampOrbit(s: Spherical, bounds: OrbitBounds): Spherical {
  const minP = bounds.minPolar ?? 0.05;
  const maxP = bounds.maxPolar ?? Math.PI - 0.05;
  const minR = bounds.minDistance ?? 0.1;
  const maxR = bounds.maxDistance ?? Number.POSITIVE_INFINITY;
  return {
    azimuth: clampAzimuth(s.azimuth, bounds.minAzimuth, bounds.maxAzimuth),
    polar: Math.min(maxP, Math.max(minP, s.polar)),
    radius: Math.min(maxR, Math.max(minR, s.radius))
  };
}

export function applyInertia(state: InertiaState, dt: number, damping: number): InertiaState {
  const vAz = Math.abs(state.vAz) < VEL_EPS ? 0 : state.vAz * damping;
  const vPo = Math.abs(state.vPo) < VEL_EPS ? 0 : state.vPo * damping;
  const vR = Math.abs(state.vR) < VEL_EPS ? 0 : state.vR * damping;
  return {
    azimuth: state.azimuth + state.vAz * dt,
    polar: state.polar + state.vPo * dt,
    radius: state.radius * (1 + state.vR * dt),
    vAz,
    vPo,
    vR
  };
}
